import { Badge } from '@/components/ui/badge'
import * as Card from '@/components/ui/card'
import { SetupImageCarousel } from '@components/SetupImageCarousel'
import { ChartCandlestick, Play } from 'lucide-solid'
import { For, Match, Switch, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Flex, Grid } from 'styled-system/jsx'
import Visualisation from '../../Visualisation'
import { useSingle } from '../state'
import { Image } from './Image'
import Live from './Live'

import { api } from '@api'
import { createQuery } from '@tanstack/solid-query'

type ActiveView = 'live' | 'wireframe' | number

export default function SetupViewer() {
  const [openImage, setOpenImage] = createSignal<number>()
  const { setupQuery } = useService(useSingle)()

  const rawImages = () => [
    { title: 'Macro', src: setupQuery.data?.image_macro },
    { title: 'Trend', src: setupQuery.data?.image_trend },
    { title: 'Pattern', src: setupQuery.data?.image_pattern },
    { title: 'Trigger', src: setupQuery.data?.image_trigger },
    { title: 'Time Stop', src: setupQuery.data?.image_time_stop },
    { title: 'Volume by price', src: setupQuery.data?.image_volume_by_price },
  ].filter((image): image is { title: string; src: string } => typeof image.src === 'string' && image.src.length > 0)

  const imagesQuery = createQuery(() => ({
    queryKey: ['setup-images-batch', setupQuery.data?.id],
    queryFn: async () => {
      const images = rawImages()
      const supabasePaths = images.map(img => img.src).filter(src => !src.startsWith('http'))
      const signedUrlsMap: Record<string, string> = {}

      if (supabasePaths.length > 0) {
        const { data, error } = await api().storage
          .from('setup-images')
          .createSignedUrls(supabasePaths, 3600)

        if (!error && data) {
          data.forEach((item) => {
            if (item.signedUrl && item.path) signedUrlsMap[item.path] = item.signedUrl
          })
        } else {
          console.error('Failed to fetch batch signed URLs in Single View:', error)
        }
      }

      return images.map(img => {
        if (img.src.startsWith('http')) return { title: img.title, src: img.src }
        return { title: img.title, src: signedUrlsMap[img.src] || '' }
      }).filter(img => img.src !== '')
    },
    enabled: !!setupQuery.data?.id && rawImages().length > 0,
    staleTime: 1000 * 60 * 50,
    gcTime: 1000 * 60 * 60,
  }))

  const resolvedImages = () => imagesQuery.data || []

  const CORE_TITLES = ['Macro', 'Trend', 'Pattern', 'Trigger']
  const coreImages = () => resolvedImages()
    .map((image, index) => ({ image, index }))
    .filter(({ image }) => CORE_TITLES.includes(image.title))
  const extraImages = () => resolvedImages()
    .map((image, index) => ({ image, index }))
    .filter(({ image }) => !CORE_TITLES.includes(image.title))

  const defaultViewIndex = () => {
    const patternIndex = resolvedImages().findIndex((img) => img.title === 'Pattern')
    return patternIndex >= 0 ? patternIndex : 0
  }

  const [activeView, setActiveView] = createSignal<ActiveView>(defaultViewIndex())

  const thumbnailStyle = (isActive: boolean) => css({
    flex: '0 0 calc(20% - 10px)',
    minWidth: '160px',
    aspectRatio: '16/9',
    border: 'solid 1px',
    borderColor: isActive ? 'jade.10' : 'bg.subtle',
    borderRadius: 'l2',
    transition: 'border-color 0.2s',
    cursor: 'pointer',
    overflow: 'hidden',
  })

  return (
    <>
      <SetupImageCarousel
        images={resolvedImages()}
        openImage={openImage}
        setIsOpen={setOpenImage}
      />

      <Card.Root
        display="grid"
        gridTemplateRows="1fr auto"
        gap={3}
        p="3"
        class={css({
          height: '100%',
          minHeight: 0,
        })}
      >
        <Box
          class={css({
            border: 'solid 1px',
            borderColor: 'bg.subtle',
            borderRadius: 'l2',
            overflow: 'hidden',
            minHeight: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <Switch
            fallback={
              <Box class={css({ width: '100%', height: '100%' })}>
                {resolvedImages()[activeView() as number] && (
                  <Image
                    image={resolvedImages()[activeView() as number]}
                    onClick={() => setOpenImage(activeView() as number)}
                    objectFit="cover"
                  />
                )}
              </Box>
            }
          >
            <Match when={activeView() === 'live'}>
              <Live />
            </Match>
            <Match when={activeView() === 'wireframe'}>
              <Visualisation
                height="100%"
                setup={setupQuery.data!}
              />
            </Match>
          </Switch>
        </Box>

        <Flex
          gap={3}
          overflowX="auto"
          pb={2}
          class={css({
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--colors-border)',
              borderRadius: 'full',
            },
          })}
        >
          <Box
            onClick={() => setActiveView('live')}
            class={thumbnailStyle(activeView() === 'live')}
          >
            <Grid justifyItems="center" alignContent="center" alignItems='center' gap='0' height="100%" gridTemplateRows='1fr auto' padding={2}>
              <ChartCandlestick />
              <Badge justifySelf='start' variant="subtle" size="md">
                Live Chart
              </Badge>
            </Grid>
          </Box>

          <For each={coreImages()}>
            {(item) => (
              <Box
                onClick={() => setActiveView(item.index)}
                class={thumbnailStyle(activeView() === item.index)}
              >
                <Image image={item.image} objectFit="cover" />
              </Box>
            )}
          </For>

          <Box
            onClick={() => setActiveView('wireframe')}
            class={thumbnailStyle(activeView() === 'wireframe')}
          >
            <Grid justifyItems="center" alignContent="center" alignItems='center' gap='0' height="100%" gridTemplateRows='1fr auto' padding={2}>
              <Play />
              <Badge justifySelf='start' variant="subtle" size="md">
                Wireframe
              </Badge>
            </Grid>
          </Box>

          <For each={extraImages()}>
            {(item) => (
              <Box
                onClick={() => setActiveView(item.index)}
                class={thumbnailStyle(activeView() === item.index)}
              >
                <Image image={item.image} objectFit="cover" />
              </Box>
            )}
          </For>
        </Flex>
      </Card.Root>
    </>
  )
}
